import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDict } from "@/utils/useDict";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildAllergyInsightCharts,
  buildAllergyPatientRows,
} from "src-next/modules/clinical-insights/transformers/mapAllergyInsights";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { fetchInsightAllergies } from "src-next/modules/clinical-insights/services/fetchInsightOrganizationBundle";
import { useFhirInsightView } from "src-next/modules/clinical-insights/hooks/useFhirInsightView";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("alergias");

function allergyPatientIds(allergies: fhir5.AllergyIntolerance[]): string[] {
  return allergies.map((allergy) => allergy.patient?.reference || "");
}

export function AllergiesAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const dict = useDict;
  const warm = fetchWarm ?? isActive;

  const allergyDict = useMemo(
    () => ({
      clinicalStatus: dict.clinicalStatus,
      criticality: dict.criticality,
      category: dict.category,
    }),
    [dict.clinicalStatus, dict.criticality, dict.category]
  );

  const view = useFhirInsightView({
    analysisId: "alergias",
    organizationId: organizationId || "",
    dateRange,
    enabled: warm,
    isActive,
    onLoadPhaseChange,
    selectPatientIds: allergyPatientIds,
    fetcher: () =>
      fetchInsightAllergies({
        organizationId: organizationId || "",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
  });

  const allergies = useMemo(() => {
    if (practitionerFilter === "all") return view.resources;
    return view.resources.filter((allergy) =>
      matchesPractitionerFilter(practitionerFilter, allergy.recorder?.reference)
    );
  }, [view.resources, practitionerFilter]);

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, secondaryItems, rankingItems, total } = useMemo(
    () => buildAllergyInsightCharts(allergies, allergyDict),
    [allergies, allergyDict]
  );

  const patientRows = useMemo(
    () => buildAllergyPatientRows(allergies, view.patients, allergyDict),
    [allergies, view.patients, allergyDict]
  );

  const statusLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of statusItems) map[item.key] = item.label;
    return map;
  }, [statusItems]);

  const secondaryLabelByKey = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of secondaryItems) map[item.key] = item.label;
    return map;
  }, [secondaryItems]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% das ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const busy = view.isLoading || view.patientsLoading;

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#e91e63"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={view.loadPhase}
        patientsProgress={view.patientsProgress}
        hasData={view.hasPayload || allergies.length > 0}
        isEmpty={total === 0 && !busy}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => {
          void view.refresh();
        }}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        secondaryStatusChart={{
          title: "Criticidade",
          items: secondaryItems,
        }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={{ ...statusLabelByKey, ...secondaryLabelByKey }}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
